"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 space-y-8">
        <div className="h-16 bg-muted/30 animate-pulse rounded" />
        <div className="aspect-video bg-muted/30 animate-pulse rounded" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/30 animate-pulse rounded w-3/4" />
          <div className="h-4 bg-muted/30 animate-pulse rounded w-full" />
          <div className="h-4 bg-muted/30 animate-pulse rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (error || !posts.length) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto px-6 py-16 space-y-12">
      {}
      <header className="space-y-6">
        <h1 className="text-5xl md:text-6xl font-bold text-foreground leading-tight">
          {post.title}
        </h1>
        
        {}
        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-accent" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </time>
            </div>
          )}
          
          {post.author?.name && (
            <div className="flex items-center gap-2">
              <User className="size-4 text-accent" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="relative aspect-video overflow-hidden rounded-sm shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <Separator className="bg-border" />

      {}
      {post.content && (
        <div 
          className="prose prose-invert max-w-none
                     prose-headings:text-foreground prose-headings:font-display
                     prose-h2:text-3xl prose-h2:font-bold prose-h2:mt-12 prose-h2:mb-6
                     prose-h3:text-2xl prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-4
                     prose-p:text-foreground prose-p:leading-relaxed prose-p:mb-6
                     prose-a:text-accent prose-a:no-underline hover:prose-a:underline
                     prose-strong:text-foreground prose-strong:font-semibold
                     prose-ul:text-foreground prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-2
                     prose-ol:text-foreground prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-2
                     prose-li:text-foreground
                     prose-blockquote:border-l-4 prose-blockquote:border-accent 
                     prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-muted-foreground
                     prose-code:text-accent prose-code:bg-muted/20 prose-code:px-2 prose-code:py-1 prose-code:rounded
                     prose-pre:bg-muted/30 prose-pre:border prose-pre:border-border"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      <Separator className="bg-border" />

      {}
      {post.author && (
        <footer className="bg-primary/60 backdrop-blur-sm rounded-sm p-8 shadow-soft">
          <div className="flex items-start gap-6">
            <Avatar className="size-20 border-2 border-accent">
              {post.author.avatar?.["96"] && (
                <AvatarImage 
                  src={post.author.avatar["96"]} 
                  alt={post.author.name}
                />
              )}
              <AvatarFallback className="bg-muted text-foreground text-xl font-semibold">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-2">
              <h3 className="text-xl font-bold text-foreground">
                About {post.author.name}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Author at PolyCon
              </p>
            </div>
          </div>
        </footer>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <section id="single-post-template-section" className="bg-primary min-h-screen py-16">
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}